import { useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import { useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { launchBulkAction } from '@juvare/platform-sdk-react/ui/features/bulk-actions';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { Box, Typography } from '@mui/material';
import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { archiveAlertsBulkActionType } from '../../../../../backend/features/alerts/bulk-actions';
import alertImage from '../../../../assets/alertImage.svg';
import { showAlertNotificationRowDialog } from '../../../../components/AlertNotificationRowDialog';
import { useAlertUiTranslation } from '../../../../hooks';
import { AlertConfigContext } from '../../../../providers';
import { getAlertBulkActionParams } from '../../../../utils';
import { useAlertCreateButton, useAlertRefreshButton, useAlertsTable } from '../../hooks';
import { AlertView } from '../AlertView';
import { AlertsList } from '../AlertsList';
const defaultStaticFilterData = { statuses: ['sent', 'sending', 'failed'] };
export const AlertsListSent = ({ mainAction, staticFilterData, title }) => {
    var _a;
    const { t } = useAlertUiTranslation();
    const { alertsTableCustomization } = React.useContext(AlertConfigContext);
    const defaultMainAction = useAlertCreateButton();
    const filterConfig = useFilterConfig((builder) => {
        var _a;
        const search = builder.quickSearchFilter({ dataPath: 'searchText', label: t('common.search') });
        const sentBy = builder.quickFilterSelect({
            dataPath: 'sentByMe',
            label: t('pages.tables.filters.sentBy'),
            values: [{ label: t('pages.tables.filters.anyone') }, { value: true, label: t('pages.tables.filters.me') }],
            widget: 'form.radio',
        });
        const notificationType = builder.quickFilterMultiSelect({
            dataPath: 'notificationType',
            label: t('pages.tables.filters.notificationType'),
            valueProviderType: 'classifier.messageType',
            widget: 'form.checkboxList',
        });
        const sender = builder.quickFilterMultiSelect({
            dataPath: 'sender',
            label: t('pages.tables.filters.sender'),
            valueProviderType: 'alerts.owners',
            widget: 'form.multiSelect',
        });
        builder.allFiltersPanel({
            children: [
                search,
                builder.allFiltersWidget({
                    type: 'form.text',
                    key: 'subject',
                    label: t('common.subject'),
                    emptyAsNull: true,
                }),
                sender,
                sentBy,
                notificationType,
            ],
        });
        builder.allFiltersPanel({
            label: t('common.dateSent'),
            children: [
                builder.allFiltersWidget({
                    type: 'form.dateTimePicker',
                    key: 'dateSentFrom',
                    dateLabel: t('common.fromDate'),
                    timeLabel: t('common.fromTime'),
                }),
                builder.allFiltersWidget({
                    type: 'form.dateTimePicker',
                    key: 'dateSentTo',
                    dateLabel: t('common.toDate'),
                    timeLabel: t('common.toTime'),
                }),
            ],
        });
        (_a = alertsTableCustomization === null || alertsTableCustomization === void 0 ? void 0 : alertsTableCustomization.extendFilters) === null || _a === void 0 ? void 0 : _a.call(alertsTableCustomization, builder);
    }, [alertsTableCustomization, t]);
    const resolvedStaticFilterData = Object.assign(Object.assign({}, defaultStaticFilterData), staticFilterData);
    const resolvedMainAction = mainAction !== null && mainAction !== void 0 ? mainAction : defaultMainAction;
    const secondaryAction = useAlertRefreshButton(() => reload());
    const resolvedTitle = title !== null && title !== void 0 ? title : t('pages.tables.tabs.sent');
    const handleRowClickDialog = (alertRowId) => {
        showAlertNotificationRowDialog({
            type: 'alert',
            onClearActiveRow: clearActiveRow,
            onReload: reload,
            label: t('common.backToNotifications'),
            rowId: alertRowId,
        });
    };
    const { dataTableProps, appliedFilterData, clearActiveRow, reload } = useAlertsTable({
        useDataTableFunction: useDataTableUsingUrl,
        staticFilterData: resolvedStaticFilterData,
        filterConfig: filterConfig,
        defaultSortColumnKey: 'sendDateTime', // what happens to failed alerts that do not have sendDateTime
        handleRowClickDialog,
    });
    const selectedAlertIds = (_a = dataTableProps.selectedRowIds) !== null && _a !== void 0 ? _a : [];
    const handleArchive = () => {
        const count = selectedAlertIds.length > 0 ? selectedAlertIds.length : dataTableProps.filteredRowCount;
        showConfirmDialog({
            title: t('archiveAlertsDialog.title', { count }),
            content: t('archiveAlertsDialog.message', { count, page: 'Sent Archived' }),
            confirmLabel: t('common.archive'),
            onConfirm: () => {
                launchBulkAction({
                    type: archiveAlertsBulkActionType,
                    title: t('archiveAlertsDialog.progress', { count }),
                    params: getAlertBulkActionParams(dataTableProps, appliedFilterData, resolvedStaticFilterData),
                    onClose: () => {
                        var _a;
                        clearActiveRow();
                        (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                        reload();
                    },
                });
            },
        });
    };
    const actions = [
        {
            icon: React.createElement(ArchiveOutlinedIcon, null),
            label: t('common.archive'),
            onClick: handleArchive,
        },
    ];
    return (React.createElement(AlertsList, Object.assign({}, dataTableProps, { title: resolvedTitle, noAlertsLabel: t('common.thereAreNoSentAlerts'), localActions: actions, mainAction: resolvedMainAction, secondaryAction: secondaryAction }), dataTableProps.activeRowId ? (React.createElement(AlertView, { id: dataTableProps.activeRowId, onReload: (clearActiveAlert) => {
            if (clearActiveAlert) {
                clearActiveRow();
            }
            reload();
        } })) : (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, px: 2, py: '180px', alignItems: 'center' } },
        React.createElement("img", { src: alertImage, alt: '' }),
        React.createElement(Typography, { variant: 'subtitle1' }, t('common.countSentAlertsSelected', { count: selectedAlertIds.length }))))));
};
